import React, { forwardRef } from 'react';
import { styled, FormControlLabel, Radio, SvgIcon } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import Text from 'shell/components/Text';

const CustomRadio = styled(Radio)(({ theme }) => {
    return {
        padding: theme.spacing(2),
        '&:focus-within': {
            background: theme.palette.ensightful.secondary[100],
        },
        '&:hover': {
            background: theme.palette.ensightful.primary[50],
        },
        '&:active': {
            background: theme.palette.ensightful.primary[100],
        },
        '& > input': {
            width: 40,
            height: 40,
        },
    };
});

const CustomFormControlLabel = styled(FormControlLabel)(({ $disabled, theme }) => {
    return {
        gap: theme.spacing(1),
        margin: 0,
        ...($disabled && {
            pointerEvents: 'none',
        }),
    };
});

const getIcon = (theme, checked, error, disabled) => {
    if (checked) {
        return disabled ? (
            <SvgIcon>
                <circle cx={'12'} cy={'12'} r={'12'} fill={theme.palette.ensightful.neutral[300]} />
                <circle cx={'12'} cy={'12'} r={'6'} fill={theme.palette.ensightful.neutral[500]} />
            </SvgIcon>
        ) : (
            <SvgIcon>
                <circle cx={'12'} cy={'12'} r={'12'} fill={theme.palette.ensightful.primary[500]} />
                <circle cx={'12'} cy={'12'} r={'6'} fill={theme.palette.ensightful.shades[0]} />
            </SvgIcon>
        );
    } else {
        if (disabled) {
            return (
                <SvgIcon>
                    <circle
                        cx={'12'}
                        cy={'12'}
                        r={'11.5'}
                        fill={theme.palette.ensightful.neutral[300]}
                        stroke={theme.palette.ensightful.neutral[400]}
                    />
                </SvgIcon>
            );
        } else {
            return error ? (
                <SvgIcon>
                    <rect width={'24'} height={'24'} fill={'transparent'} />
                    <circle
                        cx={'12'}
                        cy={'12'}
                        r={'11.5'}
                        fill={theme.palette.ensightful.error[50]}
                        stroke={theme.palette.ensightful.error[500]}
                    />
                </SvgIcon>
            ) : (
                <SvgIcon>
                    <circle
                        cx={'12'}
                        cy={'12'}
                        r={'11.5'}
                        fill={theme.palette.ensightful.shades[0]}
                        stroke={theme.palette.ensightful.neutral[500]}
                    />
                </SvgIcon>
            );
        }
    }
};

export default forwardRef(({ disabled, error, label, labelPlacement, ...rest }, ref) => {
    const theme = useTheme();

    const ControlComponent = (
        <CustomRadio
            ref={ref}
            checkedIcon={getIcon(theme, true, error, disabled)}
            disabled={disabled}
            icon={getIcon(theme, false, error, disabled)}
            inputProps={{ 'aria-disabled': disabled }}
            {...rest}
        />
    );

    let labelColor = 'default';
    if (error) labelColor = 'error';
    if (disabled) labelColor = 'disabled';

    return label ? (
        <CustomFormControlLabel
            control={ControlComponent}
            $disabled={disabled}
            disableTypography
            label={
                typeof label === 'string' ? (
                    <Text color={labelColor} component={'span'} variant={'p2'}>
                        {label}
                    </Text>
                ) : (
                    label
                )
            }
            labelPlacement={labelPlacement}
        />
    ) : (
        ControlComponent
    );
});
